import { prepareTiles } from "./lianxi-yufa-tile-readings.js";
import { wireYufaComment } from "./lianxi-yufa-comment.js";
import { feedbackYufa } from "./lianxi-yufa-feedback.js";
import { YufaExercise } from "./lianxi-yufa.js";
import { practicePlan } from "../fuxi/yufa-practice-plan.js";
import { itemGate, tasting } from "./lianxi-gate.js";
import { keptBlock } from "./lianxi-yufa-block-data.js";
import { optionsOf, yufaPart } from "./lianxi-yufa-item.js";
import { optionRow, pressedRow } from "./lianxi-xuanze.js";
import { showPlaced } from "./lianxi-yufa-placing.js";
import { markPrompts } from "./lianxi-yufa-prompt.js";
import { loadPlace, wantsPlace } from "./lianxi-place.js";
import { soundYufa } from "./lianxi-yufa-sounds.js";
import { wireTiles } from "./lianxi-yufa-tiles.js";

/**
 * Starting the grammar exercise on a `/lianxi/yufa/` page, or on the
 * handler's page for the same exercise.
 *
 * `partials/lianxi/yufa.html` draws the block with every item in it, and
 * nothing is fetched: the sentences and their answers are in the page. The
 * block is marked ready once the exercise is built, which is what the
 * browser case waits for, and a block missing a piece is left as the markup
 * drew it, which says what the exercise is.
 *
 * **A signed-in reader's place is kept.** `lianxi-place.ts` asks the
 * handler for it first and writes it onto the block, with
 * `data-cb-lianxi-record` saying where to post. The exercise then starts at
 * the item the handler left it at, and reports each answer through
 * `lianxi-yufa-recorder.ts`. A block with no place on it marks in the
 * browser and keeps nothing.
 */

/** The block, carrying every item. */
const exerciseSelector = "[data-cb-lianxi-yufa]";

/** Build the exercise inside one block. */
function openExercise(block: HTMLElement, fetching: typeof fetch): void {
  const items = [
    ...block.querySelectorAll<HTMLFormElement>("form[data-cb-yufa-item]"),
  ];
  const progress = yufaPart(block, "progress");
  const finished = yufaPart(block, "finished");
  const finishedLine = yufaPart(block, "finished-line");
  const again = yufaPart(block, "again");

  if (items.length === 0 || !progress || !finished || !finishedLine) {
    return;
  }

  const kept = keptBlock(block, fetching);
  wireYufaComment(block, kept?.keeper);
  const exercise = new YufaExercise({
    items,
    progress,
    finished,
    finishedLine,
    kept: kept?.kept,
    keeper: kept?.keeper,
    gate: itemGate(block),
    taste:
      tasting(block) && Number.isFinite(Number(block.dataset["cbLianxiTaste"]))
        ? Number(block.dataset["cbLianxiTaste"])
        : undefined,
    plan: practicePlan(
      JSON.parse(block.dataset["cbYufaPlan"] ?? "null") as unknown,
      items.length,
    ),
  });

  for (const form of items) {
    yufaPart(form, "next")?.addEventListener("click", () => {
      exercise.next();
    });
    // A press anywhere on an option's row chooses it, and marks it if it is
    // right. The radio button's own click, which an arrow key or the label
    // gives it, only moves the choice (`./lianxi-xuanze.ts`).
    optionsOf(form).forEach((radio, option) => {
      radio.closest(optionRow)?.addEventListener("click", (event) => {
        if (pressedRow(event)) {
          exercise.choose(form, option);
        }
      });
      radio.addEventListener("change", () => {
        showPlaced(form);
      });
    });
  }

  again?.addEventListener("click", () => {
    exercise.again();
  });
  prepareTiles(block);
  wireTiles(block, (form, option) => {
    exercise.choose(form, option);
  });
  markPrompts(block);
  feedbackYufa(block);

  if (kept !== undefined) {
    soundYufa(block);
  }

  exercise.start();
  block.dataset["cbLianxiReady"] = "";
}

/**
 * Start the exercise on every block under `root`.
 * @param root - The page, or the part of it to look in.
 * @param fetching - How to post. The window's own unless a test says otherwise.
 */
export function startYufa(
  root: ParentNode,
  fetching: typeof fetch = (input, init): Promise<Response> =>
    fetch(input, init),
): void {
  for (const block of root.querySelectorAll<HTMLElement>(exerciseSelector)) {
    if (block.hasAttribute("data-cb-yufa-custom")) {
      const privateFetch: typeof fetch = async (input, init) => {
        const response = await fetching(input, init);
        if (response.status === 401 || response.status === 403) {
          const privatePage = block.closest("[data-cb-custom-page]") ?? block;
          privatePage.replaceChildren();
          const note = document.createElement("p");
          note.textContent =
            "Sign in with Pro to resume this saved set. Standard practice remains available.";
          const link = document.createElement("a");
          link.href = "/lianxi/";
          link.textContent = "Continue standard practice";
          privatePage.append(note, link);
        }
        return response;
      };
      openExercise(block, privateFetch);
      continue;
    }
    if (wantsPlace(block)) {
      void loadPlace(block, fetching).then(() => {
        openExercise(block, fetching);
      });
    } else {
      openExercise(block, fetching);
    }
  }
}
